import React, { useState, useEffect } from 'react';
import { Menu, User, Film, Heart, Search } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { useAuth } from '@/contexts/AuthContext';
import { usePaymentMode } from '@/contexts/PaymentModeContext';
import { Button } from '@/components/ui/button';
import { InstantSearch } from '@/components/InstantSearch';

export const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user } = useAuth();
  const paymentMode = usePaymentMode();
  const location = useLocation();
  const navigate = useNavigate();
  const isFavoritesPage = location.pathname === '/favoris';
  
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  return (
    <nav
      data-testid="navigation-menu"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-black/90 backdrop-blur-xl shadow-lg shadow-black/20' : 'bg-black'
      }`}
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      <div className="px-4 md:px-8 lg:px-12 xl:px-16 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3 md:gap-4">
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <button
                className="p-2 hover:bg-white/10 rounded-md transition-colors"
                data-testid="mobile-menu-button"
              >
                <Menu className="w-6 h-6 text-white" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="bg-bg-secondary border-white/10 w-[280px]">
              <div className="py-6">
                <h2 className="font-bebas text-2xl text-netflix-red mb-6">Menu</h2>
                <div className="mb-6 space-y-2">
                  <Link to="/" onClick={() => setMobileMenuOpen(false)}>
                    <button
                      className={`w-full text-left px-4 py-3 rounded-md font-work font-semibold transition-colors flex items-center gap-2 ${
                        !isFavoritesPage
                          ? 'bg-netflix-red text-white'
                          : 'text-text-secondary hover:bg-white/10 hover:text-white'
                      }`}
                    >
                      <Film className="w-5 h-5" />
                      Films
                    </button>
                  </Link>
                  
                  {user && (
                    <Link to="/favoris" onClick={() => setMobileMenuOpen(false)}>
                      <button
                        className={`w-full text-left px-4 py-3 rounded-md font-work font-semibold transition-colors flex items-center gap-2 ${
                          isFavoritesPage
                            ? 'bg-netflix-red text-white'
                            : 'text-text-secondary hover:bg-white/10 hover:text-white'
                        }`}
                      >
                        <Heart className="w-5 h-5" />
                        Mes Favoris
                      </button>
                    </Link>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
          
          <Link to="/" onClick={() => window.location.href = '/'}>
            <h1 className="font-bebas text-2xl md:text-3xl lg:text-4xl font-bold text-netflix-red tracking-tight cursor-pointer hover:text-netflix-red-hover transition-colors">
              WEBEOS
            </h1>
          </Link>
        </div>
        
        <div className="flex-1 max-w-xs md:max-w-md mx-2 md:mx-8">
          <InstantSearch />
        </div>
        
        <div className="flex items-center gap-2 md:gap-4">
          {user ? (
            <div className="flex items-center gap-3">
              {paymentMode.enabled && user.is_trial && user.time_remaining_seconds > 0 && (
                <div className="hidden md:flex items-center gap-2 px-3 py-2 bg-green-900/30 border border-green-500/50 rounded-md">
                  <span className="text-green-200 font-work text-sm font-semibold">
                    Essai gratuit : {Math.floor(user.time_remaining_seconds / 60)}min
                  </span>
                </div>
              )}
              <Link to="/compte">
                <button className="flex items-center gap-2 px-3 md:px-4 py-2 bg-netflix-red hover:bg-netflix-red-hover rounded-md transition-colors">
                  <User className="w-5 h-5 text-white" />
                  <span className="hidden md:inline text-white font-work text-sm font-semibold">Compte</span>
                </button>
              </Link>
            </div>
          ) : (
            <Link to="/login">
              <Button className="bg-netflix-red hover:bg-netflix-red-hover text-sm md:text-base">
                Connexion
              </Button>
            </Link>
          )}
        </div>
      </div>
    </nav>
  );
};
